/**
 * Hacer una foto con la cámara del dispositivo (Cordova) y devolverla como data URL, lista para
 * los formularios online que suben la imagen en base64 (registro de gastos: `rFiles` con
 * `ignorarOffline` y el botón «Nuevo gasto» del listado).
 *
 * Usa `cordova-plugin-media-capture` (la cámara nativa del sistema, que ya está en la APK para el
 * audio) y `cordova-plugin-file` para leer el fichero que deja. La foto se reduce a `maxLado`
 * píxeles de lado mayor y se recomprime a JPEG: una foto de móvil son 3-5 MB que la IA no necesita
 * y que tardan en subir por datos; con 2000 px un ticket se lee igual de bien.
 */
const CODIGO_CANCELADO = 3; // CaptureError.CAPTURE_NO_MEDIA_FILES: el usuario cerró la cámara sin foto

function leerComoDataUrl(mediaFile) {
    return new Promise((resolve, reject) => {
        const url = mediaFile.localURL || ('file://' + mediaFile.fullPath);
        window.resolveLocalFileSystemURL(url, (entry) => {
            entry.file((file) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = () => reject(new Error('No se ha podido leer la foto.'));
                reader.readAsDataURL(file);
            }, () => reject(new Error('No se ha podido abrir la foto.')));
        }, () => reject(new Error('No se ha encontrado la foto.')));
    });
}

/**
 * Reduce una imagen (data URL) a `maxLado` píxeles de lado mayor y la devuelve como JPEG. Si ya es
 * más pequeña, o no es una imagen que el navegador sepa pintar, se devuelve tal cual.
 */
export function reducirImagen(dataUrl, maxLado = 2000, calidad = 0.85) {
    return new Promise((resolve) => {
        if (!dataUrl || !/^data:image\//.test(dataUrl)) {
            resolve(dataUrl);
            return;
        }
        const img = new Image();
        img.onload = () => {
            const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
            if (escala >= 1) {
                resolve(dataUrl);
                return;
            }
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * escala);
            canvas.height = Math.round(img.height * escala);
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', calidad));
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
    });
}

/**
 * Abre la cámara y devuelve `{nombre, extension, _base64}` (null si el usuario la cierra sin hacer
 * foto). Solo en Cordova: en web se usa el `<input type="file">` de siempre.
 */
export function capturarFotoBase64(opciones = {}) {
    return new Promise((resolve, reject) => {
        const captura = navigator.device && navigator.device.capture;
        if (!captura || typeof captura.captureImage !== 'function') {
            reject(new Error('La cámara no está disponible en este dispositivo.'));
            return;
        }
        captura.captureImage((mediaFiles) => {
            const mediaFile = mediaFiles && mediaFiles[0];
            if (!mediaFile) {
                resolve(null);
                return;
            }
            leerComoDataUrl(mediaFile)
                .then((dataUrl) => reducirImagen(dataUrl, opciones.maxLado, opciones.calidad))
                .then((dataUrl) => {
                    const ahora = new Date();
                    const sello = ahora.toISOString().replace(/[-:T]/g, '').slice(0, 14);
                    resolve({
                        nombre: 'foto-' + sello,
                        // La extensión sale del contenido real: si `reducirImagen` no ha convertido
                        // (ya cabía, o el WebView no sabe pintarla) la foto puede ser PNG o HEIC, y
                        // etiquetarla .jpg rompería miniaturas y previsualizaciones.
                        extension: extensionDe(dataUrl),
                        _base64: dataUrl
                    });
                })
                .catch(reject);
        }, (error) => {
            if (error && error.code === CODIGO_CANCELADO) {
                resolve(null);
            } else {
                reject(new Error('No se ha podido hacer la foto' + (error && error.code !== undefined ? ' (código ' + error.code + ')' : '') + '.'));
            }
        }, { limit: 1 });
    });
}

/** Extensión que corresponde al mime de un data URL (`jpg` si no se reconoce). */
export function extensionDe(dataUrl) {
    const mime = ((/^data:([^;,]+)[;,]/.exec(String(dataUrl || '')) || [])[1] || '').toLowerCase();
    const porMime = {
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/heic': 'heic',
        'image/heif': 'heic',
        'image/gif': 'gif',
        'image/bmp': 'bmp',
        'application/pdf': 'pdf'
    };
    return porMime[mime] || 'jpg';
}

export function hayCamara() {
    return !!(navigator.device && navigator.device.capture && typeof navigator.device.capture.captureImage === 'function');
}
